import React, { useState } from 'react';
import {
    Box, Typography, Button, TextField, Stack, Pagination, Card, CardContent,
    Grid, Chip, IconButton, InputAdornment, alpha, Dialog, DialogTitle,
    DialogContent, DialogActions
} from '@mui/material';
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButton as IonicButton, IonIcon } from '@ionic/react';
import { add, search as searchIcon, medkit, cash, calendar } from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import * as ProcedureService from '../../../api/procedure.service';
import { Procedure } from '../../../models/procedure';
import { DataTable } from '../../../components/DataTable';
import ProcedureModal from './ProcedureModal';
import ProcedureDetailModal from './ProcedureDetailModal';

const ProcedureList: React.FC = () => {
    const history = useHistory();
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [procedures, setProcedures] = useState<Procedure[]>([]);
    const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [stats, setStats] = useState({ total: 0, totalRevenue: 0, avgFee: 0 });

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedProcedureForEdit, setSelectedProcedureForEdit] = useState<Procedure | null>(null);

    // Delete confirmation dialog state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [procedureToDelete, setProcedureToDelete] = useState<string | null>(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');

    // Detail modal state
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [selectedProcedure, setSelectedProcedure] = useState<Procedure | null>(null);

    // Fetch procedures
    React.useEffect(() => {
        fetchProcedures();
    }, [page, search]);

    const fetchProcedures = async () => {
        setLoading(true);
        setError('');
        try {
            const payload = {
                page,
                limit: 10,
                search: search || ''
            };
            const response = await ProcedureService.getProcedures(payload);

            if (response && response.data) {
                const procedureData = response.data.procedures || response.data || [];
                setProcedures(procedureData);
                setPagination({
                    total: response.data.total || procedureData.length,
                    totalPages: response.data.pages || 1
                });

                // Calculate stats
                const totalRevenue = procedureData.reduce((sum: number, p: Procedure) =>
                    sum + parseFloat(p.base_fee || '0'), 0);
                setStats({
                    total: procedureData.length,
                    totalRevenue,
                    avgFee: procedureData.length > 0 ? totalRevenue / procedureData.length : 0
                });
            } else {
                setProcedures([]);
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch procedures');
            setProcedures([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        setProcedureToDelete(id);
        setDeleteConfirmText('');
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (procedureToDelete && deleteConfirmText.toLowerCase() === 'delete') {
            try {
                await ProcedureService.deleteProcedure(procedureToDelete);
                setDeleteDialogOpen(false);
                setProcedureToDelete(null);
                setDeleteConfirmText('');
                await fetchProcedures();
            } catch (err) {
                console.error('Failed to delete procedure:', err);
                alert('Failed to delete procedure');
            }
        }
    };

    const handleCancelDelete = () => {
        setDeleteDialogOpen(false);
        setProcedureToDelete(null);
        setDeleteConfirmText('');
    };

    const handleOpenCreateModal = () => {
        setSelectedProcedureForEdit(null);
        setModalOpen(true);
    };

    const handleOpenEditModal = (procedure: Procedure) => {
        setSelectedProcedureForEdit(procedure);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedProcedureForEdit(null);
    };

    const handleModalSuccess = () => {
        fetchProcedures(); // Refresh the list
    };

    const handleOpenDetailModal = (procedure: Procedure) => {
        setSelectedProcedure(procedure);
        setDetailModalOpen(true);
    };

    const handleCloseDetailModal = () => {
        setDetailModalOpen(false);
        setSelectedProcedure(null);
    };

    const handleEditFromDetail = () => {
        if (selectedProcedure) {
            setDetailModalOpen(false);
            setSelectedProcedureForEdit(selectedProcedure);
            setModalOpen(true);
        }
    };

    const columns = [
        {
            id: 'procedure_code',
            label: 'Code',
            minWidth: 120,
            format: (value: string) => (
                <Chip
                    label={value}
                    size="small"
                    sx={{
                        bgcolor: alpha('#0066cc', 0.1),
                        color: '#0066cc',
                        fontWeight: 600,
                        fontFamily: 'monospace'
                    }}
                />
            )
        },
        {
            id: 'name',
            label: 'Procedure Name',
            minWidth: 250,
            format: (value: string) => (
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#1a1a1a' }}>
                    {value}
                </Typography>
            )
        },
        {
            id: 'description',
            label: 'Description',
            minWidth: 200,
            format: (value: string) => (
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.secondary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: 200
                    }}
                >
                    {value}
                </Typography>
            )
        },
        {
            id: 'base_fee',
            label: 'Base Fee',
            minWidth: 120,
            align: 'right' as const,
            format: (value: string) => (
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 600,
                        color: '#16a34a',
                        fontSize: '0.95rem'
                    }}
                >
                    ₦{Number(value).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                </Typography>
            )
        },
        {
            id: 'created_at',
            label: 'Created',
            minWidth: 120,
            format: (value: string) => (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {new Date(value).toLocaleDateString('en-NG')}
                </Typography>
            )
        },
        {
            id: 'actions',
            label: 'Actions',
            minWidth: 180,
            align: 'center' as const,
            format: (_: any, row: Procedure) => (
                <Stack direction="row" spacing={1} justifyContent="center">
                    <Button
                        size="small"
                        variant="outlined"
                        sx={{
                            borderColor: '#0066cc',
                            color: '#0066cc',
                            '&:hover': {
                                borderColor: '#0052a3',
                                bgcolor: alpha('#0066cc', 0.05)
                            }
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDetailModal(row);
                        }}
                    >
                        View
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        sx={{
                            borderColor: '#f59e0b',
                            color: '#f59e0b',
                            '&:hover': {
                                borderColor: '#d97706',
                                bgcolor: alpha('#f59e0b', 0.05)
                            }
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEditModal(row);
                        }}
                    >
                        Edit
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(row.geacloud_procedures_id);
                        }}
                    >
                        Delete
                    </Button>
                </Stack>
            )
        }
    ];

    const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
        setPage(1);
    };

    return (
        <IonPage className='pageDown'>
            <IonHeader>
                <IonToolbar style={{
                    '--background': 'linear-gradient(135deg, #0066cc 0%, #0052a3 100%)',
                    '--color': '#ffffff'
                }}>
                    <IonTitle style={{ fontWeight: 600 }}>Medical Procedures</IonTitle>
                    <IonicButton
                        slot="end"
                        onClick={handleOpenCreateModal}
                        style={{
                            '--background': '#ffffff',
                            '--color': '#0066cc',
                            fontWeight: 600
                        }}
                    >
                        <IonIcon icon={add} slot="start" />
                        New Procedure
                    </IonicButton>
                </IonToolbar>
            </IonHeader>
            <IonContent style={{ '--background': '#f8fafc' }}>
                <Box sx={{ p: 3 }}>
                    {/* Stats Cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={4}>
                            <Card sx={{
                                background: 'linear-gradient(135deg, #0066cc 0%, #0052a3 100%)',
                                color: 'white',
                                boxShadow: '0 4px 12px rgba(0, 102, 204, 0.2)'
                            }}>
                                <CardContent>
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <IonIcon icon={medkit} style={{ fontSize: '2.5rem' }} />
                                        <Box>
                                            <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                                {stats.total}
                                            </Typography>
                                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                                Total Procedures
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Card sx={{
                                background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                                color: 'white',
                                boxShadow: '0 4px 12px rgba(22, 163, 74, 0.2)'
                            }}>
                                <CardContent>
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <IonIcon icon={cash} style={{ fontSize: '2.5rem' }} />
                                        <Box>
                                            <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                                ₦{stats.totalRevenue.toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                                            </Typography>
                                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                                Total Revenue
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Card sx={{
                                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                color: 'white',
                                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.2)'
                            }}>
                                <CardContent>
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <IonIcon icon={calendar} style={{ fontSize: '2.5rem' }} />
                                        <Box>
                                            <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                                ₦{stats.avgFee.toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                                            </Typography>
                                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                                Average Fee
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Search Bar */}
                    <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                        <CardContent>
                            <TextField
                                fullWidth
                                placeholder="Search procedures by name or code..."
                                variant="outlined"
                                value={search}
                                onChange={handleSearchChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IonIcon icon={searchIcon} style={{ fontSize: '1.5rem', color: '#64748b' }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        bgcolor: '#f8fafc',
                                        '&:hover': {
                                            bgcolor: '#f1f5f9'
                                        }
                                    }
                                }}
                            />
                        </CardContent>
                    </Card>

                    {/* Data Table */}
                    <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: 2 }}>
                        <CardContent sx={{ p: 0 }}>
                            {loading ? (
                                <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <Typography color="text.secondary">Loading procedures...</Typography>
                                </Box>
                            ) : error ? (
                                <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <Typography color="error">{error}</Typography>
                                </Box>
                            ) : !procedures || procedures.length === 0 ? (
                                <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <IonIcon icon={medkit} style={{ fontSize: '4rem', color: '#cbd5e1', marginBottom: '1rem' }} />
                                    <Typography color="text.secondary">No procedures found.</Typography>
                                    <Button
                                        variant="contained"
                                        sx={{ mt: 2, bgcolor: '#0066cc' }}
                                        onClick={handleOpenCreateModal}
                                    >
                                        Create First Procedure
                                    </Button>
                                </Box>
                            ) : (
                                <>
                                    <DataTable
                                        columns={columns}
                                        rows={procedures}
                                        onRowClick={(row) => handleOpenDetailModal(row)}
                                    />

                                    {pagination.totalPages > 1 && (
                                        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', borderTop: '1px solid #e2e8f0' }}>
                                            <Pagination
                                                count={pagination.totalPages}
                                                page={page}
                                                onChange={handlePageChange}
                                                color="primary"
                                                showFirstButton
                                                showLastButton
                                                sx={{
                                                    '& .MuiPaginationItem-root': {
                                                        fontWeight: 500
                                                    }
                                                }}
                                            />
                                            <Typography variant="body2" sx={{ ml: 3, color: 'text.secondary' }}>
                                                Showing {procedures.length} of {pagination.total} procedures
                                            </Typography>
                                        </Box>
                                    )}
                                </>
                            )}
                        </CardContent>
                    </Card>
                </Box>

                {/* Procedure Modal */}
                <ProcedureModal
                    open={modalOpen}
                    onClose={handleCloseModal}
                    onSuccess={handleModalSuccess}
                    procedure={selectedProcedureForEdit}
                />

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={handleCancelDelete}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 3
                        }
                    }}
                >
                    <DialogTitle sx={{
                        bgcolor: '#dc2626',
                        color: 'white',
                        fontWeight: 700
                    }}>
                        Delete Procedure
                    </DialogTitle>
                    <DialogContent sx={{ mt: 3 }}>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            Are you sure you want to delete this procedure? This action cannot be undone.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                            To confirm, please type <strong>delete</strong> below:
                        </Typography>
                        <TextField
                            fullWidth
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                            placeholder="Type 'delete' to confirm"
                            autoFocus
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2
                                }
                            }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ p: 3 }}>
                        <Button
                            onClick={handleCancelDelete}
                            sx={{ borderRadius: 2 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmDelete}
                            variant="contained"
                            color="error"
                            disabled={deleteConfirmText.toLowerCase() !== 'delete'}
                            sx={{
                                borderRadius: 2,
                                fontWeight: 600
                            }}
                        >
                            Delete Procedure
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Procedure Detail Modal */}
                <ProcedureDetailModal
                    open={detailModalOpen}
                    onClose={handleCloseDetailModal}
                    procedure={selectedProcedure}
                    onEdit={handleEditFromDetail}
                />
            </IonContent>
        </IonPage>
    );
};

export default ProcedureList;
